import { AppConstants } from '@unpispas/upp-defs';
import { GenericUtils } from '@unpispas/upp-defs';

import { dataService } from '../data';
import { DependInfo, ObjectOptions } from "./base";
import { DataObject } from "./base";
import { ObjectFactory } from './factory';

/**
 * Represents the definition and implementation of a schema-driven data object system.
 * This system dynamically defines classes and manages schema-based validation, data serialization,
 * and database relationships. It includes support for defining table columns, relations,
 * and dynamic type-safe methods for accessing and updating data.
 * 
 * Typical usage pattern is to:
 * 1. Define a schema with fields and relationships (use 'as const' for type safety)
 * 2. Generate a base class from this schema using CreateObject
 * 3. Validate the base class was created successfully
 * 4. Extend this base class with domain-specific logic and validation
 * 
 * @example
 * ```typescript
 * // 1. Define a schema with 'as const' for type safety
 * const userSchema = {
 *   name: 'USER',
 *   fields: [
 *     { name: 'username', type: 'string' },
 *     { name: 'email', type: 'string' },
 *     { name: 'status', type: 'string', default: 'AC' },
 *     { name: 'departmentId', type: 'objid' }
 *   ],
 *   relate: [
 *     { 
 *       direction: '>', 
 *       target: 'DEPARTMENT', 
 *       by: 'department', 
 *       name: 'department', 
 *       reason: 'departmentId',
 *       class: DepartmentType 
 *     }
 *   ]
 * } as const;
 * 
 * // 2. Create a base class with validation
 * const _UserClass = CreateObject(userSchema);
 * if (!_UserClass) {
 *   throw new Error("Failed to create BaseClass for '" + userSchema.name + "'");
 * }
 * 
 * // 3. Extend with domain-specific logic
 * export class User extends _UserClass {
 *   constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null) {
 *     super(userSchema, objid, data, objoptions || {});
 *   }
 *   
 *   // Implement Copy method for duplication
 *   Copy(store: Array<DataObject> = []): User {
 *     return this._Copy(store) as User;
 *   }
 *   
 *   // Override getter/setter to add business logic
 *   get status(): string | null {
 *     return this['__base__status'];
 *   }
 *   
 *   set status(value: string) {
 *     // Business logic - prevent modifications to deleted items
 *     if (this.status === 'DE') {
 *       return;
 *     }
 * 
 *     if (this.status === value) {
 *       return;
 *     }
 *     
 *     // Update the base property
 *     this['__base__status'] = value;
 *     
 *     // Additional business logic (e.g., notify related objects)
 *     if (this.department) {
 *       this.department.DoRefresh(this.table);
 *     }
 *   }
 *   
 *   // Add domain-specific methods
 *   get IsActive(): boolean {
 *     return (!!this.status && this.status === 'AC');
 *   }
 *   
 *   // Add view layer integration
 *   override get viewProxy(): UserView {
 *     return UserViewProxy(this, this.data);
 *   }
 * }
 * ```
 */

/****************************/
/* TYPE DEFINITIONS         */
/****************************/

/**
 * Represents the definition of a file URL for uploaded files.
 */
export interface urlfile {
    /**
     * The URL of the file, or `null` if not available.
     */    
    url: string | null,

    /**
     * The base64-encoded representation of the file, or `null` if not available.
     */
    b64: string | null
};

/**
 * Determines if a value is a valid `urlfile` object.
 * @param value - The value to check.
 * @returns `true` if the value is a valid `urlfile`, otherwise `false`.
 */
function _isUrlFile(value: any): value is urlfile {
    let valid = (value && typeof value === 'object');

    valid = valid && ('url' in value) && (typeof value.url === 'string' || value.url === null);
    valid = valid && ('b64' in value) && (typeof value.b64 === 'string' || value.b64 === null);
    
    return valid;
}

/****************************/
/* JSON DEFINITION          */
/****************************/

/**
 * Defines the structure of a table column in the schema.
 */
export interface _schemaColumn {
    /**
     * The name of the column.
     */
    readonly name: string;

    /**
     * An optional alias for the column.
     */
    readonly alias?: string;

    /**
     * An optional field for non database columns.
     */
    readonly volatile?: boolean;

    /**
     * The data type of the column.
     */    
    readonly type: string;   // 'string' | 'file' | 'number' | 'date' | 'boolean' | 'objid'
    
    /**
     * The default value of the column (optional).
     */
    readonly default?: any | null;

    /**
     * The class name to perform suitable castings.
     */
    readonly class?: abstract new (...args: any[]) => any;
}

/**
 * Defines the structure of a relationship between two tables.
 */
export interface _schemaRelate {
    /**
     * The direction of the relationship ('<' for parent-to-child, '>' for child-to-parent).
     */    
    readonly direction: string,  // '<' || '>'

     /**
     * The name for the related table.
     */
    readonly target: string,

    /**
     * The key used to establish the relationship on the related item.
     */
    readonly by: string,

    /**
     * The name of the relationship.
     */    
    readonly name: string,
    
    /**
     * The reason or key used to map the relationship.
     */    
    readonly reason: string

    /**
     * The chils condition for this relation
     */    
    readonly child: boolean

    /**
     * The class name to perform suitable castings.
     */
    readonly class?: abstract new (...args: any[]) => any; 
}

/**
 * Defines the schema for a database table.
 */
export interface SchemaTable {
    /**
     * The name of the table.
     */    
    readonly name: string,

    /**
     * An array of column definitions for the table.
     */    
    readonly fields: readonly _schemaColumn[],
    
    /**
     * An array of relationships defined for the table.
     */    
    readonly relate: readonly _schemaRelate[]
}

/**
 * Determines if the value is valid based on the type definition
 */
function _isValidType(value: any, type: string): boolean {
    if ((value == null) || (typeof value === "undefined")){
        return true;    // we always allow null values
    }

    switch (type) {
        case 'string': 
            return typeof value === 'string';
        case 'file': 
            return _isUrlFile(value);
        case 'objid':
            return (value instanceof DataObject) || (typeof value === 'string' && /^\d+$/.test(value));
        case 'number': 
            return typeof value === 'number';
        case 'boolean': 
            return typeof value === 'boolean';
        case 'date': 
            return value instanceof Date || !isNaN(Date.parse(value));
    }

    throw new Error("[SCHEMA] Unsuported item type '" + type + "'")
}

/****************************/
/* TYPE DEFINITION          */
/****************************/

/**
 * Expands schema columns into a type-safe object with proper field types.
 */
type ExpandColumns<T extends { fields: readonly _schemaColumn[]; relate: readonly _schemaRelate[] }> = {
    objid: string,
    created: Date,
    updated: Date
} & {
    [K in T['fields'][number] as K['name']] 
    : K['type'] extends 'string' ? string | null
    : K['type'] extends 'file' ? string | null
    : K['type'] extends 'number' ? number | null
    : K['type'] extends 'boolean' ? boolean | null
    : K['type'] extends 'date' ? Date | null
    : any;
};

/**
 * Excludes certain columns based on relationships from the expanded type.
 */
type ExcludeColumns<T extends { fields: readonly _schemaColumn[]; relate: readonly _schemaRelate[] }> = 
    Omit<ExpandColumns<T>, T['relate'][number]['reason']>;

/**
 * Expands schema relationships into type-safe getters and methods.
 */    
type ExpandRelates<T extends { relate: readonly _schemaRelate[]; fields: readonly _schemaColumn[] }> = 
    // 1:N relations
    { [K in T['relate'][number] as K['direction'] extends '<' ? K['reason'] : never]: 
        K extends { class: abstract new (...args: any[]) => any } ? Array<InstanceType<K["class"]>> : Array<DataObject>; 
    } &
    { [K in T['relate'][number] as K['direction'] extends '<' ? `Add${Capitalize<K['name']>}` : never]: (child: DataObject) => void; } &
    { [K in T['relate'][number] as K['direction'] extends '<' ? `Del${Capitalize<K['name']>}` : never]: (child: DataObject) => void; } &

    // 1:1 relations
    { [K in T['relate'][number] as K['direction'] extends '>' ? K['name'] : never]: 
        K extends { class: abstract new (...args: any[]) => any } ? (InstanceType<K["class"]> | null) : (DataObject | null); 
    };

/**
 * Combines columns and relationships into a type-safe schema interface.
 */
export type SchemaToInterface <T extends { 
    fields: readonly _schemaColumn[]; 
    relate: readonly _schemaRelate[] 
} > = ExcludeColumns<T> & ExpandRelates<T>;

/****************************/
/* ADJUST CLASS CASTINGS    */
/****************************/

export type SchemaToSpecific<T extends SchemaTable> = SchemaToInterface<T>;

/**
 * Abstract constructor of `BaseClass<T>` produced by the schema factory
 * before field and relation members are applied in the type system.
 */
type SchemaCtor<T extends SchemaTable> = abstract new (
    schema: T,
    objid: string | null,
    data: dataService,
    objoptions: ObjectOptions
) => BaseClass<T>;

/**
 * Abstract constructor returned by `CreateObject`: `BaseClass` plus typed
 * schema fields and relations. Domain classes extend it and implement `Copy`.
 */
export type SchemaClass<T extends SchemaTable> = abstract new (
    schema: T,
    objid: string | null,
    data: dataService,
    objoptions: ObjectOptions
) => BaseClass<T> & SchemaToSpecific<T>;

/****************************/
/* DYNAMIC BASE CLASS       */
/****************************/

/**
 * Represents a dynamic schema-based data object.
 * This class extends `DataObject` and provides functionality for:
 * - Dynamic property definition.
 * - Schema validation.
 * - Data serialization and deserialization.
 * - Management of relationships and database interactions.
 *
 * @template T - A schema type extending `SchemaTable`.
 */
export abstract class BaseClass <T extends SchemaTable > extends DataObject {    
    /**
     * Dynamic property storage.
     */    
    [key: string]: any;

    /**
     * Internal key-value store for object properties.
     */
    _data: Record<string, any> = {};

    /**
     * Creates a new instance of the BaseClass.
     * @param schema - The schema definition for the class.
     * @param objid - The object identifier, or `null` for new objects.
     * @param data - The data service for managing object persistence.
     * @param objoptions - Configuration options for the object.
     */    
    constructor(private readonly schema: SchemaTable, objid: string | null, data: dataService, objoptions: ObjectOptions){
        super(schema.name, objid, data, objoptions);

        this._data['objid'] = null;
        this._data['created'] = new Date();
        this._data['updated'] = new Date();

        for(const _field of schema.fields){
            this._data[_field.alias || _field.name] = null;
        }

        this._schema = schema;  // provide the schema to the base class
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    /**
     * Converts a value to its schema-defined type.
     *
     * @param value - The value to convert.
     * @param type - The type to convert the value into.
     * @returns The converted value, or `null` if the value is invalid.
     * @throws Error if the type is unsupported.
     * @private
     */    
    private async _toType(value: any, type: string){
        if (!_isValidType(value, type)){
            return null;
        }

        if (type == 'file'){
            const _file: urlfile = value ? { url: value.url, b64: value.b64 }:  { url: null, b64: null };

            let _base64 = _file.b64;
            if (_base64 == null){
                const _uploadurl = AppConstants.baseURL + AppConstants.uploadPath;
                if (_file.url){
                    if (_file.url.startsWith(_uploadurl)){
                        _file.url = GenericUtils.uploadToMysql(_file.url);
                    }
                    else {
                        const _dataurl = await GenericUtils.imageTobase64(_file.url);
                        if (_dataurl){
                            _base64 = _dataurl;
                        }
                    }
                }
            }

            if (_base64 != null){
                _file.b64 =  _base64.split(',', 2)[1];
            }

            return _file;
        }

        if (value){
            switch(type){
                case 'string': return value;
                case 'objid': return value;
                case 'number': return value.toString();
                case 'date': return GenericUtils.dateStrToMysql(value);
                case 'boolean': return value ? '1' : '0';
            }
        }

        return null;
    }

    /**
     * Asynchronously computes the changes to be committed based on the current schema.
     *
     * @returns A promise resolving to an object containing the computed changes.
     * @throws Error if the schema is not defined.
     */    
    protected get Change(): any {
        return (async () => {
            if (!this.schema){
                throw new Error("[SCHEMA] Schema is not defined!");
            }

            const _change: any = {};
            for(const _field of this.schema.fields){
                if (_field.volatile){
                    continue;
                }

                _change[_field.name] = await this._toType(this._data[_field.alias || _field.name], _field.type);
            }
            return _change;
        });
    }

    /**
     * Retrieves the dependency information for this object.
     *
     * @returns A `DependInfo` object mapping dependencies to their relationship details.
     * @throws Error if the schema is not defined.
     */    
    protected get Depend(): DependInfo{
        const _depend: DependInfo  = {};

        if (!this.schema){
            throw new Error("[SCHEMA] Schema is not defined!");
        }

        for(const _relate of this.schema.relate){
            if (_relate.direction === '>'){
                let _relate_reason = _relate.reason;
                
                // resolve real name by the aliased name
                for(const _field of this.schema.fields){
                    if (_field.alias && _field.alias == _relate_reason){
                        _relate_reason = _field.name;
                    }
                }

                _depend[_relate_reason] = {
                    item: this[_relate.reason],
                    relation_info: {            // this[by][to] == this
                        to: _relate_reason,     // this is the property of the related item
                        by: _relate.name        // this is the access to the related item
                    }
                }
            }
        }

        return _depend;
    }

    /**
     * Retrieves the child objects related to this instance.
     *
     * @returns An array of `DataObject` instances representing the children.
     * @throws Error if the schema is not defined.
     */    
    protected get Children(): Array <DataObject>{
        const _children: Array <DataObject> = [];

        if (!this.schema){
            throw new Error("[SCHEMA] Schema is not defined!");
        }

        for(const _relate of this.schema.relate){
            if (_relate.child == false){
                continue;   // not a child relationship
            }

            if (_relate.direction === '>'){
                const _child = this[_relate.reason] as DataObject;
                if (_child){
                    _children.push(_child);
                }
            }

            if (_relate.direction === '<'){
                for(const _child of this[_relate.reason]){
                    _children.push(_child)
                }
            }
        }

        return _children;
    }            

    /****************************/
    /* DATA OBJECT              */
    /****************************/    

    /**
     * Sets the data for this object.
     * Updates internal fields and marks the object as requiring an update if any changes are detected.
     *
     * @param value - The new data to apply.
     */
    set Data(value: any){
        this.patchValue(this._data, 'objid', value['objid']);
        this.patchValue(this._data, 'created', value['created']);
        this.patchValue(this._data, 'updated', value['updated']);

        if (this._patchData(value)){
            this.ToUpdate = true;
        }        
    }

    /**
     * Gets the internal data of this object.
     *
     * @returns The internal `_data` store.
     */    
    get Info(): any {
        return this._data;
    }

    /**
     * Updates the internal data of this object and its relationships.
     *
     * @param value - The new data to apply.
     */    
    set Info(value: any){
        this.DoPatchValues(value);   
    }

    /****************************/
    /* DATA UPDATE              */
    /****************************/   
    
    /**
     * Converts a string value from the database into its schema-defined type.
     *
     * @param value - The value to convert.
     * @param type - The type to convert the value into.
     * @returns The converted value, or `null` if the value is invalid.
     * @throws Error if the type is unsupported.
     * @private
     */    
    private _fromType(value: string, type: string){
        if (!value){
            return null;
        }

        if (type === 'file'){
            return {
                url: GenericUtils.mysqlToUpload(value),
                b64: null
            }
        }

        if (type === 'date'){
            const _date = GenericUtils.mysqlToDateStr(value);
            if (_date){
                return new Date(Date.parse(_date));
            }
            return null;
        }

        if ((type === 'string') || (type === 'objid')){
            return String(value);
        }

        if (type === 'number'){
            return parseFloat(value);
        }

        if (type === 'boolean'){
            return (value === '1');
        }

        throw new Error("[SCHEMA] Unsuported item type '" + type + "'")
    }

    /**
     * Converts raw data from the database into a strongly typed object based on the schema.
     *
     * @param info - The raw data from the database.
     * @returns A strongly typed object representing the data.
     * @throws Error if the schema is not defined.
     * @private
     */    
    private _ddbb(info: any): ExpandColumns<T> {
        const _itemdata: Record<string, any> = {};

        if (!this.schema){
            throw new Error("[SCHEMA] Schema is not defined!");
        }

        // common fields
        _itemdata['objid'] = this._fromType(info['objid'], 'objid');
        _itemdata['created'] = this._fromType(info['created'], 'date');
        _itemdata['updated'] = this._fromType(info['updated'], 'date');

        // schema fields
        for(const _field of this.schema.fields){
            _itemdata[_field.alias || _field.name] = this._fromType(info[_field.name], _field.type);
        }

        return _itemdata as ExpandColumns<T>;
    }

    /**
     * Provides a list of the desired objects, recovered from the storage or newly created
     * 
     * @param table - The child object table name.
     * @param objids - A list of objids of the given table 
     * @private
     */      
    private _ChildrenPrefetch(table: string, objids: string[]): Array <DataObject> {
        const _children: Array <DataObject> = [];

        for (const objid of new Set <string> (objids)){
            const _child = ObjectFactory.object(table, objid, this.data, {
                partial: this.IsPartial,
                storage: this.Storage || undefined
            });

            _children.push(_child)
        }

        return _children;
    }

    /**
     * Handles updates to the internal state of the object based on new data.
     *
     * @param info - The new data to apply.
     * @protected
     */    
    protected _OnUpdate(info: any){
        if (GenericUtils.VerboseLogging(4)){
            console.log("[ON UPDATE] " + info['table'] + "@" + info['objid']);
        }

        const _data: any = this._ddbb(info);

        // patch common fields for all entries
        for (const _field of [ 'objid', 'created', 'updated']){
            if (info[_field] != null && info[_field] !== ''){
                this.patchValue(this._data, _field, _data[_field]);
            }
        }

        this.DoPatchValues(_data);  
        
        // create the children for the 1:N relations (avoid fallback for scalar fields)
        for(const _relate of this.schema.relate){
            if (_relate.direction === '<'){
                const _childIds = info[_relate.reason] ?? (Array.isArray(info[_relate.name]) ? info[_relate.name] : null);
                if (_childIds){
                    this.SetChildren(_relate.reason, this._ChildrenPrefetch(_relate.target, _childIds), _relate.by);
                }
            }
        }
    }    

    /****************************/
    /* PRIVATE METHODS          */
    /****************************/   

    /**
     * Updates the internal data of the object with values from the server.
     * 
     * @param _input - The input data to patch.
     * @returns `true` if any changes were made; otherwise, `false`.
     * @throws Error if the schema is not defined.
     * @private
     */
    private _patchData(_input: any){
        let _toUpdate = false;
        
        if (!this.schema){
            throw new Error("[SCHEMA] Schema is not defined!");
        }

        for(const _field of this.schema.fields){
            const _field_name: string = _field.alias || _field.name;
            if (!(_field_name in _input)){
                throw new Error ("[SCHEMA] field '" + (_field_name) + "' missing in input")
            }

            _toUpdate = this.patchValue(this._data, _field_name, _input[_field_name]) || _toUpdate;
        }

        return _toUpdate;
    }

    /**
     * Updates internal fields and relationships based on the provided input.
     *
     * @param _input - The input data to apply.
     * @throws Error if the schema is not defined.
     * @private
     */    
    private DoPatchValues(_input: any){
        if (!this.schema){
            throw new Error("[SCHEMA] Schema is not defined!");
        }

        this._patchData(_input);

        // update the 1:1 relations (create new objects through the factory)
        for (const _relate of this.schema.relate){
            if (_relate.direction === '>'){
                if ((_relate.reason in _input) && (_input[_relate.reason])){
                    const _related = ObjectFactory.object(_relate.target, this._data[_relate.name] || null, this.data, { 
                        partial: this.IsPartial, 
                        storage: this.Storage || undefined 
                    });
                    
                    if (_related){
                        this.SetChild(_relate.reason, _related, _relate.by)
                    }
                    else {
                        throw new Error ("[SCHEMA] class '" + _relate.target + "' missing on factory definition")
                    }
                }
                else {
                    this.SetChild(_relate.reason, null, _relate.by);
                }
            }
        }
    }
}

/****************************/
/* OBJECT FACTORY CLASS     */
/****************************/

/**
 * Dynamically defines relationship methods for a class based on schema information.
 * 
 * @template T - The schema type extending `SchemaTable`.
 * @param __prototype - The target object prototype to define the property.
 * @param related - An array of schema relationships to define.
 * @description
 * For 1:N relationships:
 * - Adds `Add<RelationName>` and `Del<RelationName>` methods for adding/removing children.
 * - Defines a getter for the list of related children.
 * For 1:1 relationships:
 * - Defines a getter and setter for the related object.
 */
function _defineRelated <T extends SchemaTable> (__prototype: any, related: readonly _schemaRelate[]){
    for (const _related of related){
        if (_related.direction == '<'){   // this is a 1:N relation
            const _fldname = _related.name.charAt(0).toUpperCase() + _related.name.slice(1).toLowerCase();
            if (_fldname == 'Child'){
                throw new Error("[SCHEMA] a child with name '" + _related.name + "' is not supported. (AddChild method already exists)");
            }

            // add item method            
            Object.defineProperty(__prototype, 'Add' + _fldname, {
                value: function (this: BaseClass<T>, item: DataObject) {
                    this.AddChild(_related.reason, item, _related.by);
                },
                enumerable: false,
                configurable: true,
                writable: false,
            });

            // del item method
            Object.defineProperty(__prototype, 'Del' + _fldname, {
                value: function (this: BaseClass<T>, item: DataObject) {
                    this.DelChild(_related.reason, item, _related.by);
                },
                enumerable: false,
                configurable: true,
                writable: false
            });

            // get item list method
            Object.defineProperty(__prototype, _related.reason, {
                get: function (this: BaseClass<T>) {
                    return this._chldlist[_related.reason] || [];
                },
                enumerable: true,
                configurable: true
            });
        }

        if (_related.direction == '>'){   // this is a 1:1 relation

             // remove the instance property for redefinition
             if (Object.prototype.hasOwnProperty.call(__prototype, _related.name)){
                delete __prototype[_related.name];
            }             

            Object.defineProperty(__prototype, _related.name, {
                // the related item get method
                get: function (this: BaseClass<T>): DataObject | null {
                    return this._children[_related.reason] as DataObject || null;
                },

                // the related item set method
                set: function (this: BaseClass<T>, value: DataObject | null) {
                    if (value !== null && !(value instanceof DataObject)){
                        throw new Error("[SCHEMA] invalid related item instance (must be an instance of related object or null)");
                    }

                    if (value !== null && value.table !== _related.target){
                        throw new Error("[SCHEMA] invalid related item instance (expected a '" + _related.target + "' table instance - obtained '" + value.table + "')");
                    }

                    if (this.SetChild(_related.name, value, _related.by)){
                        this.ToUpdate = true;
                    }
                },
                enumerable: true,
                configurable: true
            });
        }
    }
}

/**
 * Defines a property with getter and setter on a prototype or instance.
 *
 * @template T - The schema type extending `SchemaTable`.
 * @param __prototype - The target object to define the property on (either prototype or instance).
 * @param fieldtype - The type of the field as specified in the schema.
 * @param fieldname - The name of the field in the data store.
 * @param propertyname - The property name to define (could be field name or __base__ prefixed version).
 * @param enumerable - Whether the property should be enumerable.
 * @description
 * This function creates a property with getter and setter for a data field:
 * - The getter retrieves data from the internal _data store with proper type handling
 * - The setter validates the value type and updates the internal _data store
 * - The setter also marks the object as requiring an update if the value changes
 */
function _defineProperty <T extends SchemaTable> (__prototype: any, fieldtype: string, fieldname: string, propertyname: string, enumerable: boolean){
    Object.defineProperty(__prototype, propertyname, {
        get: function(this: BaseClass<T>) {
            if (fieldtype == 'file') {
                return this._data[fieldname]?.b64 || this._data[fieldname]?.url;
            }
    
            return this._data[fieldname];
        },
        set: function (this: BaseClass<T>, value: any) {
            let _value = value;
    
            if (value && (fieldtype == 'file')) {
                if (typeof value === 'string'){
                    _value = value.startsWith('data:image')
                        ? { url: null, b64: value }
                        : { url: value, b64: null };
                }
                else if (_isUrlFile(value)) {
                    _value = {
                        url: value.url,
                        b64: value.b64,
                    };
                }
            }
    
            if (!_isValidType(_value, fieldtype)) {
                throw new Error("[SCHEMA - " + this.table + "] invalid type '" + fieldtype + "' for value '" + _value + "'");
            }
    
            if (this.patchValue(this._data, fieldname, _value)) {
                this.ToUpdate = true;
            }
        },
        enumerable: enumerable,
        configurable: true
    });                
}

/**
 * Dynamically defines schema field properties for a class.
 * 
 * @template T - The schema type extending `SchemaTable`.
 * @param __prototype - The target object prototypeto define the property.
 * @param columns - An array of schema columns to define as properties.
 * @description
 * Defines getter and setter methods for each column:
 * - Ensures type validation during updates.
 * - Updates the `ToUpdate` flag if a property value is changed.
 */
function _defineColumns <T extends SchemaTable> (__prototype: any, columns: readonly _schemaColumn[]){
    for (const _column of columns){
        const _column_name: string = _column.alias || _column.name;
        const _owndescriptor = Object.getOwnPropertyDescriptor(__prototype, _column_name);

        // redefine property name to __base__<_column_name> for internal access
        let _propertyname = _column_name;
        if (_owndescriptor) {
            _propertyname = `__base__${_propertyname}`;
        }

        if (Object.getOwnPropertyDescriptor(__prototype, _propertyname)){
            continue;   // the __base__ property already defined
        }

        if ((_column.type == 'objid') && (!_owndescriptor)){
            console.warn("[SCHEMA - " + __prototype.constructor.name + "] outgoing relation probably missing for field '" + _column_name + "'");
        }

        _defineProperty<T>(__prototype, _column.type, _column_name, _propertyname, true);
    }
}

/**
 * Creates base property accessors for fields that have been overridden in derived classes.
 * This function detects which properties have been overridden by the derived class and
 * creates corresponding __base__ properties to allow access to the base implementation.
 * 
 * @template T - The schema type extending `SchemaTable`.
 * @param instance - The class instance to define base properties on.
 * @param columns - An array of schema columns to process.
 */
function _OverrideProperties <T extends SchemaTable> (self: BaseClass<T>, columns: readonly _schemaColumn[]) {
    for (const _column of columns) {
        const _column_name = (_column.alias || _column.name);
        const _propertyname = `__base__${_column_name}`;
        
        if (Object.getOwnPropertyDescriptor(Object.getPrototypeOf(self), _column_name)){
            // check if already defined (it is ok, this is a relation)
            if (Object.getOwnPropertyDescriptor(self, _propertyname)){
                continue;     
            }

            _defineProperty<T>(self, _column.type, _column_name, _propertyname, false);
        }
    }
}

/**
 * Factory for creating schema-driven classes.
 * Provides a mechanism to generate classes dynamically based on schema definitions.
 */
export class ItemsBuilder {
    constructor(){
        // nothing to do
    }

    /****************************/
    /* SCHEMA VALIDATION        */
    /****************************/

    /**
     * Validates if the provided schema adheres to the expected structure and field types.
     * Ensures that table columns and relationships are correctly defined.
     * 
     * @param schema - The schema to validate.
     * @returns `true` if the schema is valid; otherwise, `false`.
     * @throws Error if the schema is invalid.
     */    
    private _checkSchema(schema: any){
        let _valid = (!!schema);

        _valid = _valid && (typeof schema.name === 'string'); 
        _valid = _valid && Array.isArray(schema.fields);
        _valid = _valid && Array.isArray(schema.relate);

        _valid = _valid && schema.fields.every((_field: any)=> {
            let _check = true;

            _check = _check && (typeof _field.name === 'string');
            _check = _check && ['string', 'file', 'number', 'date', 'boolean', 'objid'].includes(_field.type);
            
            if (!_check){
                console.error("[SCHEMA] invalid field in", _field);    
            }

            return _check;
        })

        _valid = _valid && schema.relate.every((_relate: any) => {
            let _check = true;

            _check = _check && ['<', '>'].includes(_relate.direction);
            _check = _check && (typeof _relate.target === 'string');
            _check = _check && (typeof _relate.by === 'string');
            _check = _check && (typeof _relate.name === 'string');
            _check = _check && (typeof _relate.reason === 'string');

            if (!_check){
                console.error("[SCHEMA] invalid relation in", _relate);    
            }

            return _check;
        })

        return _valid;
    }
    
    /**
     * Creates a base class dynamically based on the provided schema.
     * Defines properties and methods for interacting with schema fields and relationships.
     * 
     * @template T - The schema type extending `SchemaTable`.
     * @param schema - The schema definition.
     * @returns An abstract constructor of `BaseClass<T>` (`Copy` is implemented by the domain subclass).
     * @throws Error if the schema is invalid.
     */    
    CreateBase <T extends SchemaTable> (schema: T): SchemaCtor<T> {
        if (!this._checkSchema(schema)){
            throw new Error("[SCHEMA] Invalid schema definition");
        }

        abstract class CreatedBase extends BaseClass<T> {
            constructor(schema: T, objid: string | null, data: dataService, objoptions: ObjectOptions){
                super(schema, objid, data, objoptions);

                _OverrideProperties<T>(this, schema.fields);
            }
        }

        _defineRelated<T>(CreatedBase.prototype, schema.relate);
        _defineColumns<T>(CreatedBase.prototype, schema.fields);

        return CreatedBase;
    }  

    /**
     * Creates a dynamic class based on a schema definition.
     * The class includes type-safe properties, relationships, and validation based on the schema.
     * 
     * @template T - The schema type extending `SchemaTable`.
     * @param _schema - The schema definition.
     * @returns `SchemaClass<T>` — `BaseClass` plus typed fields and relations from the schema.
     * @example
     * ```typescript
     * const UserClass = CreateObject(userSchema);
     * const user = new UserClass(null, dataService, {});
     * user.username = "john.doe";
     * user.email = "john.doe@example.com";
     * await user.Save();
     * ```
     */    
    CreateObject<T extends SchemaTable>(_schema: T): SchemaClass<T> {
        return this.CreateBase(_schema) as SchemaClass<T>;
    }
}

/****************************/
/* OBJECT FACTORY INSTANCE  */
/****************************/

/**
 * Represents a factory for creating schema-based objects.
 * Provides dynamic class creation based on schema definitions.
 */
let itemsBuilder: ItemsBuilder | null = null;

/**
 * Creates a dynamic class based on a schema definition.
 * @param _schema - The schema definition.
 * @returns A dynamically created class with schema-based properties and methods.
 */
export function CreateObject<T extends SchemaTable>(_schema: T) {
    if (!itemsBuilder){
        itemsBuilder = new ItemsBuilder();
    }

    return itemsBuilder.CreateObject(_schema);
}
